/**
 * Alegerea furnizorului unei facturi externe: o singură casetă, în care se scrie ori numele,
 * ori codul fiscal.
 *
 * Până acum câmpul era text liber, iar aceeași tipografie intra o dată „Print Expert SRL" și
 * altă dată „PRINT EXPERT" — două rânduri în raportul de costuri, niciunul cu totalul adevărat.
 *
 * Nu există buton „caută la ANAF" și nu există două câmpuri. Omul știe fie cum îi spune
 * furnizorului, fie ce cod are pe factură, și n-are de ce să decidă dinainte după care caută.
 * Serverul se uită în nomenclator, în ExpertAccounts, în facturile vechi și — doar dacă s-a
 * tastat un cod și nu l-a găsit nicăieri — la ANAF.
 *
 * Lista se randează în `document.body`. Formularul stă într-o fereastră cu `overflow-y-auto`,
 * care ar tăia-o după primul rând — exact ce s-a întâmplat la importul de sell-out (14.09.2026).
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { AddClientModal, type PrecompletarePartener } from '../orders/AddClientModal'

export interface FurnizorAles {
  id: number
  nume: string
}

type Sursa = 'crm' | 'ea' | 'istoric' | 'anaf'

interface Rezultat {
  id: number | null
  nume: string
  cif: string | null
  tara: string
  adresa: string | null
  oras: string | null
  judet: string | null
  regCom: string | null
  platitorTva: boolean | null
  sursa: Sursa
  inEa: boolean
  facturiVechi?: number
  eaSyncStatus?: string
  inactiv?: boolean
}

export interface FurnizorCreat {
  id: number
  nume: string
  cif: string | null
  eaSyncStatus: string
  ea: 'trimis' | 'exista deja' | 'neconfigurat' | 'esuat'
  eaMesaj?: string
}

/** De unde vine rândul — se vede pe fiecare propunere, ca să se știe ce se alege. */
const ETICHETE: Record<Sursa, { text: string; clasa: string }> = {
  crm: { text: 'în CRM', clasa: 'bg-b4l-blue/10 text-b4l-blue' },
  ea: { text: 'în EA', clasa: 'bg-emerald-100 text-emerald-700' },
  istoric: { text: 'folosit deja', clasa: 'bg-amber-100 text-amber-700' },
  anaf: { text: 'ANAF', clasa: 'bg-purple-100 text-purple-700' },
}

/**
 * Ce i se spune omului după creare. Trimiterea în ERP poate să nu fi plecat, iar asta nu se
 * ascunde: furnizorul e creat oricum în CRM, dar dacă în ERP n-a ajuns, contabilitatea trebuie
 * să afle acum, nu peste o lună când caută partenerul și nu-l găsește.
 */
export function mesajDupaCreare(f: FurnizorCreat): { fel: 'ok' | 'atentie'; text: string } {
  switch (f.ea) {
    case 'trimis':
      return { fel: 'ok', text: `„${f.nume}” a fost creat și trimis în ExpertAccounts.` }
    case 'exista deja':
      return {
        fel: 'ok',
        text: `„${f.nume}” a fost adăugat în CRM. În ExpertAccounts exista deja.`,
      }
    case 'neconfigurat':
      return {
        fel: 'atentie',
        text: `„${f.nume}” a fost creat în CRM, dar nu a ajuns în ExpertAccounts: legătura pentru parteneri noi nu e configurată (Setări › Integrări › ExpertAccounts).`,
      }
    default:
      return {
        fel: 'atentie',
        text: `„${f.nume}” a fost creat în CRM, dar ExpertAccounts l-a refuzat: ${f.eaMesaj ?? 'fără detalii'}`,
      }
  }
}

interface Pozitie {
  left: number
  width: number
  top?: number
  bottom?: number
  maxHeight: number
}

const MIN_SPATIU = 220
const MAX_INALTIME = 320

export function CautaFurnizor({
  value,
  onChange,
  disabled,
}: {
  value: FurnizorAles | null
  onChange: (f: FurnizorAles | null) => void
  disabled?: boolean
}) {
  const [text, setText] = useState('')
  /*
    Furnizorul nou se adaugă în fereastra comună de parteneri (spec parteneri comuni, §5), cu
    aceleași reguli — pleacă în EA la salvare. Țara se CERE, nu se presupune: registrul ăsta e
    făcut pentru Google Ireland, Meta Ireland, TikTok UK.
  */
  const [fereastra, setFereastra] = useState<PrecompletarePartener | null>(null)
  const [rezultate, setRezultate] = useState<Rezultat[]>([])
  const [cauta, setCauta] = useState(false)
  const [deschis, setDeschis] = useState(false)
  const [poz, setPoz] = useState<Pozitie | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const listaRef = useRef<HTMLDivElement>(null)

  const q = text.trim()

  /*
    Căutarea pornește după o pauză de tastare, nu la fiecare literă: „Print Expert" ar însemna
    douăsprezece cereri, iar una dintre ele ajunge până la ANAF.
  */
  useEffect(() => {
    if (q.length < 2) {
      setRezultate([])
      setCauta(false)
      return
    }
    let anulat = false
    setCauta(true)
    const t = setTimeout(async () => {
      try {
        const r = await api.get<{ rezultate: Rezultat[] }>('/furnizori/cauta', {
          params: { q },
        })
        if (!anulat) setRezultate(r.data.rezultate)
      } catch {
        if (!anulat) setRezultate([])
      } finally {
        if (!anulat) setCauta(false)
      }
    }, 350)
    return () => {
      anulat = true
      clearTimeout(t)
    }
  }, [q])

  useEffect(() => {
    function click(e: MouseEvent) {
      const t = e.target as Node
      if (wrapRef.current?.contains(t) || listaRef.current?.contains(t)) return
      setDeschis(false)
    }
    document.addEventListener('mousedown', click)
    return () => document.removeEventListener('mousedown', click)
  }, [])

  useLayoutEffect(() => {
    if (!deschis) {
      setPoz(null)
      return
    }
    const aseaza = () => {
      const r = wrapRef.current?.getBoundingClientRect()
      if (!r) return
      const subCamp = window.innerHeight - r.bottom - 8
      const pesteCamp = r.top - 8
      const inJos = subCamp >= MIN_SPATIU || subCamp >= pesteCamp
      setPoz({
        left: r.left,
        width: Math.max(r.width, 320),
        ...(inJos ? { top: r.bottom + 4 } : { bottom: window.innerHeight - r.top + 4 }),
        maxHeight: Math.min(MAX_INALTIME, Math.max(inJos ? subCamp : pesteCamp, 140)),
      })
    }
    aseaza()
    window.addEventListener('scroll', aseaza, true)
    window.addEventListener('resize', aseaza)
    return () => {
      window.removeEventListener('scroll', aseaza, true)
      window.removeEventListener('resize', aseaza)
    }
  }, [deschis])

  const creeaza = useMutation({
    mutationFn: async (d: Partial<Rezultat> & { nume: string }) =>
      (
        await api.post<{ furnizor: FurnizorCreat }>('/furnizori', {
          nume: d.nume,
          cif: d.cif ?? null,
          tara: d.tara ?? 'Romania',
          regCom: d.regCom ?? null,
          adresa: d.adresa ?? null,
          oras: d.oras ?? null,
          judet: d.judet ?? null,
          platitorTva: d.platitorTva ?? null,
          sursa: d.sursa ?? 'manual',
          existaInEa: d.inEa === true,
        })
      ).data.furnizor,
    onSuccess: (f) => {
      const m = mesajDupaCreare(f)
      if (m.fel === 'ok') toast.success(m.text)
      // Durată mai lungă: e un mesaj de acțiune, nu o confirmare care poate fi ratată.
      else toast.warning(m.text, { duration: 12_000 })
      onChange({ id: f.id, nume: f.nume })
      setDeschis(false)
      setText('')
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      toast.error(err?.response?.data?.error?.message ?? 'Furnizorul nu a putut fi creat.')
    },
  })

  function alege(r: Rezultat): void {
    // Deja în nomenclator: se leagă direct.
    if (r.id != null) {
      onChange({ id: r.id, nume: r.nume })
      setDeschis(false)
      setText('')
      return
    }
    // Din ERP: e deja acolo, se creează doar la noi, fără reguli și fără trimitere.
    if (r.sursa === 'ea') {
      creeaza.mutate(r)
      return
    }
    // Din ANAF sau din facturile vechi: fereastra, completată cu ce știm.
    setFereastra({
      sursa: r.sursa,
      name: r.nume,
      cif: r.cif ?? '',
      tara: r.sursa === 'anaf' ? 'Romania' : '',
      regCom: r.regCom ?? undefined,
      address: r.adresa ?? undefined,
      city: r.oras ?? undefined,
      county: r.judet ?? undefined,
      vatPayer: r.platitorTva,
    })
    setDeschis(false)
  }

  function adaugaNou(): void {
    const cod = /^\s*(ro)?\s*\d{2,10}\s*$/i.test(q)
    setFereastra(cod ? { cif: q, tara: 'Romania' } : { name: q, tara: '' })
    setDeschis(false)
  }

  const fereastraJsx = createPortal(
    <AddClientModal
      rol="furnizor"
      open={fereastra !== null}
      onClose={() => setFereastra(null)}
      precompletare={fereastra}
      onCreated={(id, nume) => {
        setFereastra(null)
        setText('')
        onChange({ id, nume })
      }}
    />,
    document.body,
  )

  if (value) {
    return (
      <div className="flex w-full items-center gap-1 rounded border border-b4l-border bg-b4l-bg-soft px-2 py-1">
        {fereastraJsx}
        <span className="flex-1 truncate text-sm" title={value.nume}>
          {value.nume}
        </span>
        <button
          type="button"
          onClick={() => onChange(null)}
          disabled={disabled}
          className="text-xs text-b4l-text-muted hover:text-b4l-red"
          title="Alege alt furnizor"
        >
          ✕
        </button>
      </div>
    )
  }

  const potiCreaDirect = q.length >= 2 && !creeaza.isPending
  const existaExact = rezultate.some((r) => r.nume.trim().toLowerCase() === q.toLowerCase())

  const lista = poz && (
    <div
      ref={listaRef}
      style={{
        position: 'fixed',
        left: poz.left,
        width: poz.width,
        top: poz.top,
        bottom: poz.bottom,
        maxHeight: poz.maxHeight,
      }}
      className="z-[70] overflow-auto rounded border border-gray-200 bg-white shadow-lg"
    >
      {cauta && <div className="px-3 py-2 text-sm text-gray-400">Se caută…</div>}

      {!cauta && q.length < 2 && (
        <div className="px-3 py-2 text-xs text-gray-400">
          Scrie numele furnizorului. Pentru o firmă din România merge și CUI-ul.
        </div>
      )}

      {!cauta &&
        rezultate.map((r) => {
          const et = ETICHETE[r.sursa]
          return (
            <button
              key={`${r.sursa}-${r.id ?? r.nume}`}
              type="button"
              onClick={() => alege(r)}
              disabled={creeaza.isPending}
              className="block w-full border-b border-gray-100 px-3 py-1.5 text-left last:border-0 hover:bg-gray-50 disabled:opacity-50"
            >
              <div className="flex items-center gap-1.5">
                <span className="flex-1 truncate text-sm">{r.nume}</span>
                <span className={`rounded px-1.5 py-0.5 text-[10px] ${et.clasa}`}>{et.text}</span>
              </div>
              <div className="text-[11px] text-b4l-text-muted">
                {r.cif ?? 'fără cod fiscal'}
                {r.tara !== 'Romania' && ` · ${r.tara}`}
                {r.facturiVechi != null &&
                  ` · ${r.facturiVechi} ${r.facturiVechi === 1 ? 'factură' : 'facturi'}`}
                {r.inactiv && <span className="ml-1 text-b4l-red">· inactivă la ANAF</span>}
                {r.sursa === 'crm' && r.eaSyncStatus !== 'synced' && (
                  <span className="ml-1 text-amber-600">· încă nu e în ExpertAccounts</span>
                )}
              </div>
            </button>
          )
        })}

      {!cauta && q.length >= 2 && rezultate.length === 0 && (
        <div className="px-3 py-2 text-sm text-gray-400">
          Nimic găsit — nici în CRM, nici în ExpertAccounts, nici la ANAF.
        </div>
      )}

      {/*
        Crearea pe numele tastat. Rămâne disponibilă și când există propuneri: o potrivire
        parțială pe nume nu înseamnă că e același furnizor.
      */}
      {potiCreaDirect && !existaExact && (
        <button
          type="button"
          onClick={adaugaNou}
          className="block w-full border-t border-gray-200 bg-b4l-bg-soft px-3 py-2 text-left text-sm hover:bg-gray-100"
        >
          <span className="font-medium">+ Adaugă „{q}”</span>
          <span className="block text-[11px] text-b4l-text-muted">
            Furnizor nou — se trimite imediat în ExpertAccounts.
          </span>
        </button>
      )}
    </div>
  )

  return (
    <div ref={wrapRef} className="relative">
      <input
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          setDeschis(true)
        }}
        onFocus={() => setDeschis(true)}
        disabled={disabled || creeaza.isPending}
        placeholder="numele furnizorului…"
        className="w-full rounded border border-b4l-border px-2 py-1 text-sm disabled:bg-gray-50"
      />
      {deschis && !disabled && lista && createPortal(lista, document.body)}
      {fereastraJsx}
    </div>
  )
}
